import React from 'react';
import { Switch, Typography } from '@mui/material';
import { useUnlayerTemplatesEditorContext } from '@/components/views/UnlayerEmailsView/UnlayerTemplatesShared/UnlayerTemplatesEditorView/UnlayerTemplatesEditorContext';
import FolioCheckbox from '@/components/FolioCheckbox';

const hasSharedBrokerGroups = (brokerGroupIds) =>
	(brokerGroupIds ?? []).map(String).filter(Boolean).length > 0;

/**
 * Active/inactive controls for AI templates in the table and edit drawer.
 * Master templates cannot be toggled and display a fallback label instead.
 * Templates must be shared with at least one broker group before they can be activated.
 */
export const AiTemplateActiveTableCell = ({
	template,
	labels,
	disabled = false,
	onToggleActive,
}) => {
	if (template?.master) {
		return <Typography variant='body2'>{labels.masterFallbackLabel}</Typography>;
	}

	const isShared = hasSharedBrokerGroups(template?.brokerGroupIds);
	const isActive = Boolean(template?.active);
	// Allow turning off an invalid active+unshared state; block turning on when unshared.
	const cannotActivate = !isShared && !isActive;

	return (
		<Switch
			size='small'
			color='primary'
			checked={isActive}
			disabled={disabled || cannotActivate}
			onClick={(event) => event.stopPropagation()}
			onMouseDown={(event) => event.stopPropagation()}
			onChange={(event) => {
				event.stopPropagation();
				onToggleActive(event.target.checked);
			}}
			inputProps={{
				'aria-label': `Toggle active status for ${template?.name ?? 'template'}`,
			}}
		/>
	);
};

export const AiTemplateActiveDrawerField = () => {
	const {
		labels,
		templateForm,
		patchTemplateValues,
		isFetchingTemplateDetail,
		isCreatingTemplate,
		isUpdatingTemplate,
		isDeletingTemplate,
		isSharingTemplate = false,
		canModifySelectedTemplate = true,
	} = useUnlayerTemplatesEditorContext();
	const { values } = templateForm;

	const templateActive = values.active;
	const isShared = hasSharedBrokerGroups(values.brokerGroupIds);
	const cannotActivate = !isShared && !templateActive;

	const isSaving =
		isCreatingTemplate || isUpdatingTemplate || isDeletingTemplate || isSharingTemplate;

	const disabled =
		isFetchingTemplateDetail || isSaving || cannotActivate || !canModifySelectedTemplate;

	return (
		<FolioCheckbox
			component={Switch}
			checked={templateActive}
			label={labels.activeDrawerTitle}
			onChange={(event) => patchTemplateValues({ active: event.target.checked })}
			disabled={disabled}
		/>
	);
};
